/**
 * @file auth.guard.ts
 * @description Route guard that protects authenticated routes.
 * Checks if the user has a valid (non-expired) JWT token.
 * Redirects to /login with returnUrl if not authenticated.
 *
 * Refresh handling: on a hard reload the `user` signal starts out `null`
 * and is only populated either by `AuthService.rehydrateUserFromStorage()`
 * (best-effort, depends on `em`/`name` being in storage) or — authoritatively
 * — once the boot `/users/get` call settles. `isAuthenticated()` requires a
 * non-null `user`, so deciding synchronously against that initial `null`
 * would bounce an authenticated user (valid token, profile still in flight)
 * to /login on every refresh. To avoid that, when the initial profile load
 * is still pending the guard returns an Observable that waits for
 * `profileLoaded` — same pattern as `admin.guard.ts`.
 */
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const decide = (): boolean => {
    if (authService.isAuthenticated()) return true;

    // Redirect to login, preserving the intended destination
    router.navigate(['/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  };

  // Profile already settled (success, error, or no session) — decide now.
  if (authService.profileLoaded()) return decide();

  // Initial /users/get is still in flight (hard refresh). Wait for it to
  // settle so we evaluate against the authoritative user snapshot instead
  // of the transient `null` that exists before the profile resolves.
  return toObservable(authService.profileLoaded).pipe(
    filter(Boolean),
    take(1),
    map(() => decide()),
  ) as Observable<boolean>;
};
